<template>
    <div>
        <div v-if="loading" class="text-center p-5"><b-spinner variant="info" /></div>
        <b-alert v-else-if="loadError" show variant="danger">
            {{ $t('label.productLoadError') }}
            <b-button size="sm" variant="outline-danger" @click="loadProduct">{{ $t('label.retryPermissionDefinitions') }}</b-button>
        </b-alert>
        <form v-else @submit.prevent="submitForm">
            <b-form-group :label="$t('label.name')"><b-form-input v-model.trim="form.name" required /></b-form-group>
            <b-form-group :label="$t('label.description')"><b-form-textarea v-model.trim="form.description" rows="4" required /></b-form-group>
            <div class="row">
                <div class="col-md-6">
                    <b-form-group :label="$t('module.productCategory')">
                        <multiselect v-model="form.category" :options="categoryOptions" track-by="value" label="text"
                            :allow-empty="false" :internal-search="false" :loading="categoryLoading"
                            :placeholder="$t('module.productCategory')" @search-change="searchCategories" />
                        <b-form-text v-if="categoryError" class="text-danger">
                            {{ $t('label.productCategoryLoadError') }}
                            <b-button type="button" size="sm" variant="outline-danger" @click="loadCategories()">{{ $t('label.retryPermissionDefinitions') }}</b-button>
                        </b-form-text>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group :label="$t('module.productCondition')">
                        <multiselect v-model="form.condition" :options="conditionOptions" track-by="value" label="text"
                            :allow-empty="false" :loading="conditionLoading" :placeholder="$t('module.productCondition')" />
                        <b-form-text v-if="conditionError" class="text-danger">
                            {{ $t('label.productConditionLoadError') }}
                            <b-button type="button" size="sm" variant="outline-danger" @click="loadConditions">{{ $t('label.retryPermissionDefinitions') }}</b-button>
                        </b-form-text>
                    </b-form-group>
                </div>
            </div>
            <b-form-group :label="$t('label.company')">
                <multiselect v-model="form.company" :options="companyDdl" track-by="value" label="text"
                    :allow-empty="false" :internal-search="false" :loading="companyLoading"
                    @input="companyChanged" @search-change="searchCompanies" />
                <b-form-text v-if="companyError" class="text-danger">
                    {{ $t('label.productCompanyLoadError') }}
                    <b-button type="button" size="sm" variant="outline-danger" @click="loadCompanies()">{{ $t('label.retryPermissionDefinitions') }}</b-button>
                </b-form-text>
            </b-form-group>
            <b-form-group :label="$t('label.picUser')">
                <multiselect v-model="form.picUser" :options="userDdl" track-by="value" label="text"
                    :allow-empty="false" :disabled="!form.company" @search-change="searchUsers" />
            </b-form-group>
            <div class="row">
                <div class="col-md-6"><b-form-group :label="$t('label.productListingEndDate')">
                    <b-form-input v-model="form.listingEndDate" type="datetime-local" step="1" required />
                    <b-form-text>{{ $t('label.productListingTimezone') }}</b-form-text>
                </b-form-group></div>
                <div class="col-md-6"><b-form-group :label="$t('label.productExpiryDate')">
                    <b-form-input v-model="form.expiryDate" type="date" />
                </b-form-group></div>
            </div>
            <b-form-group :label="$t('label.productTags')">
                <b-form-input v-model="tagsText" :placeholder="$t('label.productTagsHint')" />
            </b-form-group>
            <b-form-group :label="$t('label.image')">
                <div v-if="form.productImages.length" class="d-flex flex-wrap mb-2">
                    <div v-for="image in form.productImages" :key="image.uuid" class="mr-2 mb-2">
                        <div class="product-image-frame">
                            <a :href="image.image" target="_blank" rel="noopener noreferrer">
                                <img :src="image.image" :alt="form.name" class="product-preview rounded border"
                                    :class="{ 'image-pending-removal': form.deletedImageUUIDs.includes(image.uuid) }" />
                            </a>
                            <span v-if="form.coverImageUUID === image.uuid && form.coverImageIndex === null && !form.deletedImageUUIDs.includes(image.uuid)"
                                class="cover-ribbon">{{ $t('label.productCoverImage') }}</span>
                        </div>
                        <b-form-checkbox :key="`existing-${image.uuid}-${coverSelectionVersion}`" :checked="form.coverImageUUID === image.uuid && form.coverImageIndex === null"
                            :disabled="form.deletedImageUUIDs.includes(image.uuid)" @change="selectExistingCover(image.uuid, $event)">
                            {{ $t('label.productCoverImage') }}
                        </b-form-checkbox>
                        <b-form-checkbox v-if="productUUID" v-model="form.deletedImageUUIDs" :value="image.uuid" class="mt-1" @change="coverDeletionChanged">
                            {{ $t('label.delete') }}
                        </b-form-checkbox>
                    </div>
                </div>
                <b-form-file v-model="form.images" multiple accept="image/jpeg,image/png,image/gif,image/webp" @input="imagesChanged" />
                <div v-if="imagePreviews.length" class="mt-3">
                    <div class="d-flex flex-wrap">
                        <div v-for="(image, index) in imagePreviews" :key="image.url" class="mr-3 mb-2">
                            <div class="product-image-frame">
                                <img :src="image.url" :alt="image.file.name" class="product-preview rounded border" />
                                <span v-if="form.coverImageIndex === index" class="cover-ribbon">{{ $t('label.productCoverImage') }}</span>
                            </div>
                            <small class="d-block image-file-name">{{ image.file.name }}</small>
                            <b-form-checkbox :key="`upload-${index}-${coverSelectionVersion}`" :checked="form.coverImageIndex === index" @change="selectUploadedCover(index, $event)">{{ $t('label.productCoverImage') }}</b-form-checkbox>
                        </div>
                    </div>
                </div>
                <b-form-text v-if="productUUID">{{ $t('label.productKeepImages') }}</b-form-text>
            </b-form-group>
            <b-form-group :label="$t('label.pdf')">
                <a v-if="form.pdfUrl && !form.removePdf" :href="form.pdfUrl" target="_blank" rel="noopener noreferrer" class="d-block mb-2">{{ $t('label.openPdf') }}</a>
                <b-form-file v-model="form.pdf" accept="application/pdf" :disabled="form.removePdf" />
                <b-form-checkbox v-if="productUUID && form.pdfUrl" v-model="form.removePdf" class="mt-2" @change="value => value && (form.pdf = null)">
                    {{ $t('label.productRemovePdf') }}
                </b-form-checkbox>
                <b-form-text v-if="form.pdfUrl && !form.removePdf">{{ $t('label.keepCurrentPdf') }}</b-form-text>
            </b-form-group>
            <b-form-checkbox v-model="form.isActive" switch>{{ $t('label.active') }}</b-form-checkbox>
            <b-alert v-if="validationError" show variant="danger" class="mt-3">{{ validationError }}</b-alert>
            <div class="mt-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">{{ $t('label.productVariations') }}</h6>
                    <b-button type="button" size="sm" variant="outline-info" :disabled="!canAddVariation" @click="addVariation">
                        <i class="mdi mdi-plus mr-1"></i>{{ $t('label.addVariation') }}
                    </b-button>
                </div>
                <b-form-text class="mb-2">{{ $t('label.variationLimit', [variationLimit]) }}</b-form-text>
                <div class="table-responsive">
                    <table class="table table-bordered variation-table">
                        <thead><tr>
                            <th>{{ $t('label.name') }}</th>
                            <th>{{ $t('label.productStock') }}</th><th>{{ $t('label.variationCostPrice') }}</th>
                            <th>{{ $t('label.variationSellingPrice') }}</th><th>{{ $t('label.status') }}</th>
                            <th>{{ $t('label.action') }}</th>
                        </tr></thead>
                        <tbody>
                            <tr v-for="variation in form.variations" :key="variation.key">
                                <td><b-form-input v-model.trim="variation.name" required /></td>
                                <td>
                                    <b-form-checkbox :checked="variation.stock === null" @change="variation.stock = $event ? null : 0">
                                        {{ $t('label.productUnlimitedStock') }}
                                    </b-form-checkbox>
                                    <b-form-input v-if="variation.stock !== null" v-model.number="variation.stock" type="number" min="0" step="1" required class="mt-2" />
                                    <small v-if="variation.stock === 0" class="text-muted">{{ $t('label.productOutOfStock') }}</small>
                                </td>
                                <td><b-form-input v-model="variation.cost_price" type="number" min="0" step="0.01" required /></td>
                                <td><b-form-input v-model="variation.selling_price" type="number" min="0" step="0.01" required /></td>
                                <td><b-form-checkbox v-model="variation.is_active" switch>{{ $t('label.active') }}</b-form-checkbox></td>
                                <td><b-button v-if="!variation.uuid" type="button" size="sm" variant="outline-danger" :aria-label="$t('label.delete')" @click="removeVariation(variation.key)">
                                    <i class="mdi mdi-delete-outline"></i>
                                </b-button></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <hr>
            <div class="d-flex justify-content-end">
                <b-button type="button" variant="danger" class="mr-2" @click="$emit('done')">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="info" :disabled="!status.save">
                    <span v-if="status.save">{{ $t(productUUID ? 'label.updateX' : 'label.createX', [$t('module.product')]) }}</span>
                    <b-spinner v-else small />
                </b-button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import { productMethods, lookupMethods } from '@/state/api';
import { lookupService } from '@/services/api/lookup.service';
import { lookupModel } from '@/models/api/lookup.model';
import { MAX_PRODUCT_VARIATIONS } from '@/constants/product';

let variationKey = 0;
function newVariation() {
    return { key: ++variationKey, name: 'Default', stock: 0, cost_price: '0.00', selling_price: '', is_active: true };
}

export default {
    name: 'ProductForm', components: { Multiselect },
    props: { productUUID: { type: String, default: '' }, searchCriteria: { type: Object, required: true } },
    data() {
        return {
            imagePreviews: [], coverSelectionVersion: 0,
            companyLoading: false, companyError: false,
            variationLimit: MAX_PRODUCT_VARIATIONS,
            conditionOptions: [], conditionLoading: false, conditionError: false, conditionRequest: 0,
            categoryOptions: [], categoryLoading: false, categoryError: false, categoryTimer: null, categoryRequest: 0,
            loading: true, loadError: false, validationError: '', tagsText: '', companyTimer: null, userTimer: null,
            form: {
                uuid: '', name: '', description: '', category: null, categoryUUID: '', categoryName: '', condition: null, conditionUUID: '', conditionName: '',
                company: null, picUser: null, listingEndDate: '', expiryDate: '', price: '', availableStock: 0,
                isActive: true, images: [], coverImageIndex: null, coverImageUUID: null, productImages: [], deletedImageUUIDs: [], removePdf: false, pdf: null, pdfUrl: '', variations: this.productUUID ? [] : [newVariation()],
            },
        };
    },
    computed: {
        canAddVariation() { return this.form.variations.length < this.variationLimit; },
        ...mapState('api/product', ['status', 'details']),
        companyDdl() { return this.$store.state.api.lookup.companyDdl || []; },
        userDdl() { return this.$store.state.api.lookup.userDdl || []; },
    },
    created() { this.loadCategories(); this.loadConditions(); this.loadProduct(); },
    beforeDestroy() { clearTimeout(this.companyTimer); clearTimeout(this.userTimer); clearTimeout(this.categoryTimer); this.categoryRequest++; this.conditionRequest++; this.clearImagePreviews(); },
    methods: {
        ...productMethods, ...lookupMethods,
        optionFor(options, uuid, name) { return options.find(option => option.value === uuid) || (uuid ? { value: uuid, text: name || uuid } : null); },
        selectExistingCover(uuid, checked) {
            this.coverSelectionVersion++;
            if (!checked || this.form.deletedImageUUIDs.includes(uuid)) return;
            this.form.coverImageUUID = uuid;
            this.form.coverImageIndex = null;
        },
        selectUploadedCover(index, checked) {
            this.coverSelectionVersion++;
            if (!checked) return;
            this.form.coverImageIndex = index;
            this.form.coverImageUUID = null;
        },
        coverDeletionChanged(deleted) {
            if (!this.form.coverImageUUID || !deleted.includes(this.form.coverImageUUID)) return;
            const retained = this.form.productImages.find(image => !deleted.includes(image.uuid));
            this.form.coverImageUUID = retained?.uuid || null;
            this.form.coverImageIndex = !retained && this.form.images.length ? 0 : null;
            this.coverSelectionVersion++;
        },
        clearImagePreviews() {
            this.imagePreviews.forEach(image => URL.revokeObjectURL(image.url));
            this.imagePreviews = [];
        },
        imagesChanged(files) {
            const selected = this.imagePreviews[this.form.coverImageIndex]?.file;
            this.clearImagePreviews();
            this.form.images = files || [];
            this.imagePreviews = this.form.images.map(file => ({ file, url: URL.createObjectURL(file) }));
            const index = selected ? this.form.images.indexOf(selected) : -1;
            this.form.coverImageIndex = index >= 0 ? index : (!this.productUUID && this.form.images.length ? 0 : null);
        },
        addVariation() {
            if (this.form.variations.length < this.variationLimit) this.form.variations.push(newVariation());
        },
        removeVariation(key) {
            this.form.variations = this.form.variations.filter(variation => variation.key !== key || variation.uuid);
        },
        async loadConditions() {
            const request = ++this.conditionRequest;
            this.conditionLoading = true; this.conditionError = false;
            try {
                const result = await lookupService.getProductConditions();
                if (request !== this.conditionRequest) return;
                if (!result?.success || !Array.isArray(result.data)) throw new Error('Condition lookup failed.');
                this.conditionOptions = result.data.map(lookupModel);
            } catch (error) {
                if (request === this.conditionRequest) this.conditionError = true;
            } finally {
                if (request === this.conditionRequest) this.conditionLoading = false;
            }
        },
        async loadCategories(term = '') {
            const request = ++this.categoryRequest;
            this.categoryLoading = true; this.categoryError = false;
            try {
                const result = await lookupService.getProductCategories(term ? [term] : []);
                if (request !== this.categoryRequest) return;
                if (!result?.success || !Array.isArray(result.data)) throw new Error('Category lookup failed.');
                this.categoryOptions = result.data.map(lookupModel);
            } catch (error) {
                if (request === this.categoryRequest) this.categoryError = true;
            } finally {
                if (request === this.categoryRequest) this.categoryLoading = false;
            }
        },
        searchCategories(term) {
            clearTimeout(this.categoryTimer);
            this.categoryRequest++;
            this.categoryTimer = setTimeout(() => this.loadCategories(term), 350);
        },
        async loadProduct() {
            this.loading = true; this.loadError = false;
            try {
                await this.loadCompanies();
                if (this.productUUID) {
                    const result = await this.getProduct({ uuid: this.productUUID });
                    if (!result?.success) { this.loadError = true; return; }
                    await this.lookupUser({ companyUUID: this.details.companyUUID });
                    this.form = {
                        ...this.form, ...this.details, images: [], coverImageIndex: null, pdf: null, deletedImageUUIDs: [], removePdf: false,
                        coverImageUUID: this.details.productImages.find(image => image.is_cover_image === true || image.is_cover_image === 1 || image.is_cover_image === '1')?.uuid || null,
                        variations: this.details.variations.map(row => ({ ...row, key: ++variationKey })),
                        condition: this.optionFor(this.conditionOptions, this.details.conditionUUID, this.details.conditionName),
                        category: this.optionFor(this.categoryOptions, this.details.categoryUUID, this.details.categoryName),
                        company: this.optionFor(this.companyDdl, this.details.companyUUID, this.details.companyName),
                        picUser: this.optionFor(this.userDdl, this.details.picUserUUID, this.details.picUserName),
                    };
                    this.tagsText = this.details.tags.join(', ');
                }
            } catch (error) { this.loadError = true; }
            finally { this.loading = false; }
        },
        companyChanged(company) {
            this.form.picUser = null;
            if (company) this.lookupUser({ companyUUID: company.value });
        },
        async loadCompanies(term = '') {
            this.companyLoading = true; this.companyError = false;
            try {
                const result = await this.lookupCompany(term ? [term] : []);
                this.companyError = !result || result.success === false;
            } catch (error) { this.companyError = true; }
            finally { this.companyLoading = false; }
        },
        searchCompanies(term) {
            clearTimeout(this.companyTimer);
            this.companyTimer = setTimeout(() => this.loadCompanies(term), 350);
        },
        searchUsers(term) {
            clearTimeout(this.userTimer);
            if (!this.form.company) return;
            this.userTimer = setTimeout(() => this.lookupUser({ companyUUID: this.form.company.value, searchWords: term ? [term] : [] }), 350);
        },
        async submitForm() {
            if (!this.status.save) return;
            this.validationError = '';
            if (!this.form.category) {
                this.validationError = this.$t('message.requiredX', [this.$t('module.productCategory')]); return;
            }
            if (!this.form.condition) {
                this.validationError = this.$t('message.requiredX', [this.$t('module.productCondition')]); return;
            }
            if (!this.form.company || !this.form.picUser) {
                this.validationError = this.$t('label.productPicRequired'); return;
            }
            const variations = this.form.variations || [];
            const validNumber = value => value !== '' && value != null && Number.isFinite(Number(value)) && Number(value) >= 0;
            if (!variations.length || (!this.productUUID && variations.length > this.variationLimit) || variations.some(row =>
                !row.name?.trim() || (row.stock !== null && (!validNumber(row.stock) || !Number.isInteger(Number(row.stock)))) ||
                !validNumber(row.cost_price) || !validNumber(row.selling_price)
            )) {
                this.validationError = this.$t('label.productVariationsInvalid', [this.variationLimit]); return;
            }
            const images = this.form.images || [];
            if (this.form.coverImageUUID) {
                // Existing covers belong to the retained image list, not the uploads.
                const existing = this.form.productImages.some(image => image.uuid === this.form.coverImageUUID);
                if (!existing || this.form.deletedImageUUIDs.includes(this.form.coverImageUUID)) {
                    this.validationError = this.$t('label.productCoverInvalid'); return;
                }
                this.form.coverImageIndex = null;
            } else if (this.form.coverImageIndex != null) {
                const index = Number(this.form.coverImageIndex);
                if (this.form.coverImageIndex === '' || !Number.isInteger(index) || index < 0 || index >= images.length) {
                    this.validationError = this.$t('label.productCoverInvalid'); return;
                }
                this.form.coverImageIndex = index;
            }
            if (images.some(file => !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) ||
                (this.form.pdf && this.form.pdf.type !== 'application/pdf')) {
                this.validationError = this.$t('label.productFilesInvalid'); return;
            }
            const data = {
                ...this.form, images, categoryUUID: this.form.category.value, conditionUUID: this.form.condition.value,
                companyUUID: this.form.company.value, picUserUUID: this.form.picUser.value,
                tags: [...new Set(this.tagsText.split(',').map(tag => tag.trim()).filter(Boolean))],
            };
            const action = this.productUUID ? this.updateProduct : this.createProduct;
            const result = await action({ data, filter: this.searchCriteria });
            if (result?.success) this.$emit('done');
        },
    },
};
</script>

<style scoped>
.variation-table { min-width: 960px; }
.variation-table th, .variation-table td { min-width: 120px; vertical-align: middle; }
.image-file-name { width: 90px; overflow-wrap: anywhere; }
.image-pending-removal { opacity: 0.35; }
.product-image-frame { position: relative; width: 90px; height: 90px; overflow: hidden; border-radius: 0.25rem; }
.cover-ribbon {
    position: absolute;
    top: 14px;
    right: -32px;
    width: 112px;
    transform: rotate(45deg);
    text-align: center;
    font-size: 0.5rem;
    line-height: 1.1;
    font-weight: bold;
    text-transform: uppercase;
    padding: 4px 0;
    color: #343a40;
    background: #f1f3f5;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    pointer-events: none;
}
.product-preview { width: 90px; height: 90px; object-fit: cover; }
</style>
